import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flame, TrendingUp, MessageSquare, Handshake, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis } from "recharts";

export default function StreakBadge() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/streak/mine");
        setData(data);
      } catch (e) {}
    })();
  }, []);

  if (!data) return null;
  const days = data.streak_days || 0;

  const heading = days >= 3
    ? t("streak.streak_days", { count: days })
    : days === 0
      ? t("streak.start_today")
      : days === 1
        ? t("streak.active_singular", { count: days })
        : t("streak.active_plural", { count: days });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="streak-badge"
          aria-label={t("streak.aria_label", { count: days })}
          className={`inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-sm font-semibold transition-colors ${
            days >= 3
              ? "bg-gradient-to-r from-coral to-amber-500 text-white shadow-sm hover:brightness-110"
              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >
          <Flame size={14} className={days >= 3 ? "" : "text-coral"} />
          <span>{days}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl border-slate-200 shadow-lg" data-testid="streak-popover">
        <div className="p-4 border-b border-slate-100">
          <p className="eyebrow text-gold text-[10px]">{t("streak.eyebrow")}</p>
          <div className="flex items-center justify-between mt-1">
            <h4 className="font-display text-lg text-petroleum">
              {heading}
            </h4>
            <Flame size={20} className={days >= 3 ? "text-coral" : "text-slate-300"} />
          </div>
        </div>
        <div className="p-4 space-y-3">
          <MiniStat icon={TrendingUp} label={t("streak.publications_week")} value={(data.published_week || 0) + (data.briefs_week || 0)} />
          <MiniStat icon={MessageSquare} label={t("streak.messages_week")} value={data.messages_week} />
          <MiniStat icon={Handshake} label={t("streak.active_rooms")} value={data.active_rooms} />
          <MiniStat icon={CheckCircle2} label={t("streak.milestones_completed")} value={data.milestones_completed} />
        </div>
        <div className="px-4 pb-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">{t("streak.activity_days")}</p>
          <ResponsiveContainer width="100%" height={54}>
            <BarChart data={data.daily} margin={{ top: 2, bottom: 0, left: 0, right: 0 }}>
              <XAxis dataKey="day" tick={{ fontSize: 8, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={2} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 10 }} cursor={false} />
              <Bar dataKey="count" fill="#D4AF37" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500 inline-flex items-center gap-2">
        <Icon size={13} className="text-gold" /> {label}
      </span>
      <span className="text-sm font-display text-petroleum">{value ?? 0}</span>
    </div>
  );
}
